import GTM from 'services/GTM';
import { GTM_EVENTS, MONITORED_EXTERNAL_DOMAINS } from 'constants/tracking';

/**
 * Extracts the base domain from a hostname by taking the last two parts of the domain.
 * This helps normalize domains for comparison across different subdomains.
 *
 * For example:
 * - "app.livechat.com" becomes "livechat.com"
 * - "accounts.livechatinc.com" becomes "livechatinc.com"
 * - "example.com" remains "example.com"
 *
 * @param {string} hostname - The hostname to extract the base domain from
 * @returns {string} The base domain (last two parts joined with a dot)
 */
export function getBaseDomain(hostname: string): string {
  const parts = (hostname || '').toLowerCase().split('.').filter(Boolean);
  if (parts.length <= 2) {
    return parts.join('.');
  }

  return parts.slice(-2).join('.');
}

/**
 * Determines whether an external redirect should be tracked based on domain monitoring rules.
 *
 * This function implements a selective tracking strategy where only redirects to specific
 * external domains (defined in MONITORED_EXTERNAL_DOMAINS) are tracked.
 *
 * The function performs three key checks:
 * 1. Validates the URL format and extracts hostname information
 * 2. Compares base domains to determine if the redirect is truly external
 * 3. Checks if the external domain is in the monitored domains list
 *
 * Internal redirects (same base domain) are automatically excluded from tracking
 * as they don't need the event in the data layer.
 *
 * @param {string} url - The destination URL to evaluate for tracking
 * @returns {boolean} True if the redirect should be tracked, false otherwise
 */
const shouldTrackExternalRedirect = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    const host = parsed.hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();
    const hostBase = getBaseDomain(host);
    const currentBase = getBaseDomain(currentHost);

    if (hostBase === currentBase) {
      return false;
    }

    return MONITORED_EXTERNAL_DOMAINS.includes(hostBase);
  } catch {
    return false;
  }
};

/**
 * Normalizes a destination URL for consistent tracking by extracting only the essential parts.
 *
 * This function standardizes URLs for analytics purposes by removing query parameters,
 * fragments, and other variable components that could create noise in tracking data.
 * It preserves only the origin (protocol + hostname + port) and pathname, which are
 * the most reliable identifiers for tracking user navigation patterns.
 *
 * @param {string} url - The raw destination URL to normalize
 * @returns {string} The normalized URL containing only origin and pathname, or the original URL if parsing fails
 */
export const normalizeDestinationUrlForTracking = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);

    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url;
  }
};

/**
 * Tracks external redirects by pushing navigation events to Google Tag Manager (GTM).
 *
 * This is the main entry point for tracking user navigation to external domains.
 * It implements a comprehensive tracking workflow that includes validation, normalization,
 * and data layer push for analytics collection. The event is pushed in order to keep
 * the analysis flow consistent across our different domains in Text universe.
 *
 * The tracking workflow:
 * 1. Validates if the URL should be tracked (external + monitored domain)
 * 2. Normalizes the URL to ensure consistent data collection
 * 3. Constructs a payload with the destination URL
 * 4. Pushes the event to GTM for processing by analytics tools
 *
 * Error handling is built-in to prevent tracking failures from affecting user experience.
 * If any step fails, the function silently continues without breaking the user's navigation.
 *
 * @param {string} url - The destination URL that the user is navigating to
 * @returns {void} No return value - this is a side-effect function for analytics
 */
export const pushExternalRedirectToDataLayer = (url: string): void => {
  try {
    if (!shouldTrackExternalRedirect(url)) {
      return;
    }

    const destUrl = normalizeDestinationUrlForTracking(url);

    const payload = {
      dest_url: destUrl,
    };

    GTM.track(GTM_EVENTS.NAVIGATE_TO_PRODUCT, payload);
  } catch (error) {
    console.error(error);
  }
};
